import React from "react";
import { language } from ".";
import { PhraseKey } from "./types";
import { getNestedStringValue } from "../helpers";

interface SayProps {
  phrase: PhraseKey;
  mutator?: (template: string) => string;
  renderHtml?: boolean;
  dynamicValue?: string;
  children?: React.ReactElement | React.ReactElement[];
}

/**
 * This component displays a value from an imported language object based on a phrase.
 * The phrase is a dot-delimited key used to retrieve the value.
 * It is also rendered as a data attribute (`data-phrase`) for ease of debugging.
 *
 * @component
 * @param {SayProps} props - The properties object.
 * @param {string} [props.phrase] - The dot-delimited key used to retrieve the nested string value from the language object.
 * @param {Function} [props.mutator] - An optional function that processes the retrieved string value.
 * @param {boolean} [props.renderHtml=false] - An optional flag that determines if the value should be rendered as HTML.
 * @param {string} [props.dynamicValue] - An optional value that can override the phrase if not empty.
 * @returns {JSX.Element} A span element containing the retrieved string value;
 *   or if children are nested, the children with a data-phrase-key attribute added.
 *
 * @examples
 * <Say phrase="path.of.nested.key" />
 * <Say phrase="path.of.nested.key" mutator={(value) => value.replace("{{myNumber}}", "5")} />
 */
export default function Say({
  phrase,
  mutator,
  renderHtml = false,
  dynamicValue = "",
  children
}: SayProps): JSX.Element {
  // Use the dynamic value if provided, look up the value by phrase.
  const resolvedValue = dynamicValue || getNestedStringValue(language, phrase);
  const rawValue = resolvedValue ? resolvedValue : phrase;

  // If a dynamic value is provided, append ":dynamic" to the phrase key.
  const phraseKey = dynamicValue ? `${phrase}:dynamic` : phrase;

  if (children) {
    return <span data-phrase-key={phraseKey}>{children}</span>;
  }

  const processedValue = rawValue && mutator ? mutator(rawValue) : rawValue;

  return renderHtml ? (
    <span data-phrase-key={phraseKey} dangerouslySetInnerHTML={{ __html: processedValue }} />
  ) : (
    <span data-phrase-key={phraseKey}>{processedValue}</span>
  );
}

export { Say };
